import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { useLocation, useNavigate } from 'react-router-dom';
import ArunkubeLogo from './ArunkubeLogo';
import { useSpaceExperience, SpaceMotionControl } from './SpaceExperience';

const Header = () => {
  const space = useSpaceExperience();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onBlogs = pathname === '/blogs';
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const menuButtonRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      let active = '';
      document.querySelectorAll('main > section[id]').forEach((section) => {
        if (section.getBoundingClientRect().top <= 180) active = `#${section.id}`;
      });
      setActiveSection(active);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isMobileMenuOpen]);

  const navItems = [
    { name: 'About', href: '#about' },
    { name: 'Skills', href: '#skills' },
    { name: 'Projects', href: '#projects' },
    { name: 'Certifications', href: '#certifications' },
    { name: 'GitHub', href: '#github' },
    { name: 'Blogs', href: '/blogs' },
    { name: 'Resume', href: '#resume' },
    { name: 'Contact', href: '#contact' },
  ].map(item => ({ ...item, href: onBlogs && item.href.startsWith('#') ? `/${item.href}` : item.href }));

  const handleNavClick = (e, href) => {
    if (!href.startsWith('#')) return;
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      element.setAttribute('tabindex', '-1');
      element.focus({ preventScroll: true });
      setIsMobileMenuOpen(false);
    }
  };

  const handleHomeClick = (event) => {
    event.preventDefault();
    setIsMobileMenuOpen(false);
    if (pathname !== '/') navigate('/');
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
  };

  return (
    <>
      <motion.header
        className={`header ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.645, 0.045, 0.355, 1] }}
      >
        <div className="header-container">
          {/* Logo - Name */}
          <motion.a
            href="/"
            onClick={handleHomeClick}
            className="header-logo"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {space ? <ArunkubeLogo /> : 'Arun Bharathwaj'}
          </motion.a>

          {/* Desktop Navigation */}
          <div className="header-actions">
            <nav className="header-nav-pill" aria-label="Primary">
              {navItems.map((item, index) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="nav-link"
                  aria-current={item.href === '/blogs' && onBlogs ? 'page' : activeSection === item.href ? 'location' : undefined}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.4 }}
                  whileHover={{ y: -2 }}
                >
                  {item.name}
                </motion.a>
              ))}
            </nav>
            <ThemeToggle />
            <SpaceMotionControl />
          </div>

          {/* Mobile Menu Button */}
          <div className="header-mobile-actions">
            <ThemeToggle className="theme-toggle-mobile" />
            <SpaceMotionControl />
            <motion.button
            className="mobile-menu-button"
            ref={menuButtonRef}
            aria-controls="portfolio-mobile-nav"
            aria-label={isMobileMenuOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileTap={{ scale: 0.9 }}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          >
            <nav className="mobile-nav" id="portfolio-mobile-nav" aria-label="Mobile navigation">
              {navItems.map((item, index) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="mobile-nav-link"
                  aria-current={item.href === '/blogs' && onBlogs ? 'page' : activeSection === item.href ? 'location' : undefined}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  {item.name}
                </motion.a>
              ))}
            </nav>
            <div className="mobile-theme-row">
              <ThemeToggle />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
